import React, { useCallback, useEffect } from "react";

import { isAxiosError } from "axios";
import { format } from "date-fns";
import { FormProvider, useForm } from "react-hook-form";

import {
  GoalBasedFinancingMethodSchema,
  GoalBasedGoalTypeSchema,
} from "@fartherfinance/frontend/api/FinancialPlans/goals/types";
import {
  GoalBasedPlanDateTypeSchema,
  GoalBasedRepeatTimeFrameSchema,
} from "@fartherfinance/frontend/api/FinancialPlans/types";

import { PickGoalsFormFields } from "@src/multiCustodian/components/FinancialPlans/CreateUpdatePlanFlow/pages/Goals/Types";
import {
  DateOfBirthField,
  NameField,
  OwnerField,
  PlanningHorizonField,
  TargetRetirementAgeField,
} from "@src/multiCustodian/components/FinancialPlans/CreateUpdatePlanFlow/pages/Shared/constants";
import FormDrawer from "@src/multiCustodian/components/FinancialPlans/CreateUpdatePlanFlow/pages/Shared/FormDrawer/FormDrawer";
import { populateSelectedGoalFormFields } from "@src/multiCustodian/components/FinancialPlans/CreateUpdatePlanFlow/pages/Shared/helpers/populateSelectedGoalFormFields";
import { AnnualIncreaseFields } from "@src/multiCustodian/components/FinancialPlans/CreateUpdatePlanFlow/pages/Shared/MultiDropdown/AnnualIncrease/AnnualIncrease";
import type { ErrorMessage } from "@src/multiCustodian/components/TaxBudgets/reducer/types";
import useStatusNotification from "@src/multiCustodian/hooks/useStatusNotification";
import FlexWrapper from "@src/sharedComponents/Forms/FlexWrapper";
import FormDateField from "@src/sharedComponents/Forms/FormDateField";
import FormDropdownField from "@src/sharedComponents/Forms/FormDropdownField";
import FormNumberField from "@src/sharedComponents/Forms/FormNumberField";
import FormTextField from "@src/sharedComponents/Forms/FormTextField";
import {
  formNumberFieldCustomRegex,
  formNumberFieldRules,
} from "@src/utils/formValidation";

import { GoalDrawerProps } from "./types";

type PickedFormFields = PickGoalsFormFields<
  | "OwnerField"
  | "NameField"
  | "DateOfBirthField"
  | "PlanningHorizonField"
  | "TargetRetirementAgeField"
>;

type Form = PickedFormFields & AnnualIncreaseFields;

const RetirementGoalForm = ({
  goalBasedFinancialPlanId,
  activeGoalToEdit,
  defaultOwner,
  drawerOpen,
  ownerOptions,
  onRemove,
  onClose,
  postGoalBasedFinancialPlanGoal,
  patchGoalBasedFinancialPlanGoal,
  retirementHorizons = [],
  isLoading,
}: GoalDrawerProps) => {
  const statusNotification = useStatusNotification();

  const methods = useForm<Form>({
    mode: "all",
    reValidateMode: "onChange",
    defaultValues: {
      [OwnerField]: defaultOwner,
      [NameField]: `${defaultOwner.label}'s Retirement Goal`,
      [DateOfBirthField]:
        defaultOwner.dob === null
          ? undefined
          : new Date(`${defaultOwner.dob}T00:00:00`),
      [PlanningHorizonField]: "",
      [TargetRetirementAgeField]: "",
    },
  });

  const {
    getValues,
    reset,
    formState: { isDirty, isValid },
  } = methods;

  const setFormFields = useCallback(() => {
    if (drawerOpen) {
      if (activeGoalToEdit) {
        const formattedFormValues = populateSelectedGoalFormFields(
          defaultOwner,
          activeGoalToEdit,
          retirementHorizons
        );

        reset(formattedFormValues);
      } else {
        reset({
          [OwnerField]: defaultOwner,
          [NameField]: `${defaultOwner.label}'s Retirement Goal`,
          [DateOfBirthField]:
            defaultOwner.dob === null
              ? undefined
              : new Date(`${defaultOwner.dob}T00:00:00`),
          [PlanningHorizonField]: undefined,
          [TargetRetirementAgeField]: undefined,
        });
      }
    }
  }, [drawerOpen, activeGoalToEdit, reset, defaultOwner, retirementHorizons]);

  useEffect(() => {
    setFormFields();
  }, [setFormFields]);

  const handleOnClose = () => {
    setFormFields();
    onClose();
  };

  const handleOnSave = async () => {
    if (retirementHorizons === undefined) {
      throw new Error("retirementHorizons is undefined");
    }

    const values = getValues();

    if (values[OwnerField] === undefined) {
      return;
    }

    if (values[DateOfBirthField] === undefined) {
      return;
    }

    const body = {
      dateOfBirth: format(new Date(values[DateOfBirthField]), "yyyy/MM/dd"),
      financeMethod: GoalBasedFinancingMethodSchema.Enum.Cash,
      goalType: GoalBasedGoalTypeSchema.Enum.Retirement,
      name: values[NameField],
      owner: values[OwnerField].value,
      startDate: {
        dateType: GoalBasedPlanDateTypeSchema.Enum.Age,
        value: values[TargetRetirementAgeField],
        owner: values[OwnerField].value,
        planDateId:
          activeGoalToEdit !== undefined
            ? activeGoalToEdit.startDate?.planDateId
            : undefined,
      },
      endDate: {
        dateType: GoalBasedPlanDateTypeSchema.Enum.Age,
        value: values[PlanningHorizonField],
        owner: values[OwnerField].value,
        planDateId:
          activeGoalToEdit !== undefined
            ? activeGoalToEdit.endDate?.planDateId
            : undefined,
      },
      repeats: GoalBasedRepeatTimeFrameSchema.enum.None,
    };

    try {
      if (activeGoalToEdit) {
        await patchGoalBasedFinancialPlanGoal({
          goalBasedFinancialPlanId,
          goalBasedFinancialPlanGoalId: activeGoalToEdit.goalId,
          body,
        });
        statusNotification("Goal added", "Success");
      } else {
        await postGoalBasedFinancialPlanGoal({
          goalBasedFinancialPlanId,
          body,
        });
        statusNotification("Goal added", "Success");
      }
      onClose();
    } catch (error) {
      if (isAxiosError<ErrorMessage>(error)) {
        const message = error.response?.data.message;
        statusNotification(`Error adding goal: ${message ?? ""}`, "Error");
      } else {
        statusNotification(
          "There was an error adding the goal. Please try again.",
          "Error"
        );
      }
    }
  };

  return (
    <FormDrawer
      title={`${defaultOwner.label}'s Retirement Age`}
      drawerOpen={drawerOpen}
      onClose={handleOnClose}
      saveDisabled={isLoading || !isValid || !isDirty}
      onSave={handleOnSave}
      removeDisabled
      onRemove={onRemove}
      isLoading={isLoading}
      hasError={false}
      deleteModalTitle={"Remove Goal"}
      deleteModalSubTitle={"Are you sure you want to remove this goal?"}
    >
      <FormProvider {...methods}>
        <FlexWrapper>
          <FormDropdownField
            name={OwnerField}
            label={"Owner"}
            values={ownerOptions}
            disableSearch={true}
            disabled={activeGoalToEdit !== undefined}
            required={"Must not be empty"}
          />

          <FormTextField
            name={NameField}
            label="Name"
            required={"Must not be empty"}
          />
        </FlexWrapper>

        <FlexWrapper>
          <FormDateField
            name={DateOfBirthField}
            label="Date of Birth"
            placeholder="MM/DD/YYYY"
            required={"Must not be empty"}
          />

          <FormNumberField
            name={TargetRetirementAgeField}
            label="Target Retirement Age"
            required={"Must not be empty"}
            customRegex={formNumberFieldCustomRegex}
            rules={{
              deps: PlanningHorizonField,
              ...formNumberFieldRules({
                required: true,
                minValue: 10,
                maxValue: 150,
              }),
              validate: {
                retirementGreaterThanPlanningHorizon: (
                  retirementAge: string
                ) => {
                  const rAge = parseInt(retirementAge);

                  if (isNaN(rAge)) {
                    return "Invalid age";
                  }

                  const values = getValues();

                  const phAge = parseInt(values[PlanningHorizonField]);

                  if (isNaN(phAge)) {
                    return true;
                  }

                  if (phAge < rAge) {
                    return "Can't be greater than planning horizon";
                  }

                  return true;
                },
              },
            }}
          />
        </FlexWrapper>

        <FlexWrapper>
          <FormNumberField
            name={PlanningHorizonField}
            label="Planning Horizon (Age)"
            required={"Must not be empty"}
            customRegex={formNumberFieldCustomRegex}
            rules={{
              deps: TargetRetirementAgeField,
              ...formNumberFieldRules({
                required: true,
                minValue: 10,
                maxValue: 150,
              }),
              validate: {
                planningHorizonLessThanRetirement: (
                  planningHorizonAge: string
                ) => {
                  const phAge = parseInt(planningHorizonAge);

                  if (isNaN(phAge)) {
                    return "Invalid age";
                  }

                  const values = getValues();

                  const rAge = parseInt(values[TargetRetirementAgeField]);

                  if (isNaN(rAge)) {
                    return true;
                  }

                  if (phAge < rAge) {
                    return "Can't be less than retirement age";
                  }

                  return true;
                },
              },
            }}
          />
        </FlexWrapper>
      </FormProvider>
    </FormDrawer>
  );
};

export default RetirementGoalForm;
